import { EventBus } from "./EventBus.js";

// ── Scroll buffer ─────────────────────────────────────────────────────────────

const MAX_ENTRIES = 200;
const buffer: string[] = [];

/**
 * Append a message to the global scroll buffer and emit it on the EventBus.
 * All code that wants to show a feed line must call this — not emit directly.
 */
export function pushFeed(message: string): void {
  buffer.push(message);
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
  EventBus.emit("ui:combat_log_push", { message });
}

/** Read-only snapshot of the current buffer. Used by useCombatLog to hydrate on mount. */
export function getFeedBuffer(): readonly string[] {
  return buffer;
}

/** Clear the buffer. Intended for test teardown. */
export function clearFeedBuffer(): void {
  buffer.length = 0;
}

// ── Formatters ────────────────────────────────────────────────────────────────

export function formatAttackHit(
  attackerName: string,
  targetName: string,
  damage: number,
  damageType: string,
  isCrit: boolean,
): string {
  const critTag = isCrit ? " (CRITICAL!)" : "";
  return `${attackerName} hits ${targetName} for ${damage} ${damageType} damage${critTag}!`;
}

export function formatAttackMiss(
  attackerName: string,
  targetName: string,
  isFumble: boolean,
): string {
  const fumbleTag = isFumble ? " (fumble!)" : "";
  return `${attackerName} attacks ${targetName} but misses${fumbleTag}.`;
}

export function formatEnemySpotted(enemyName: string): string {
  return `${enemyName} has spotted you!`;
}

export function formatEntitySlain(name: string): string {
  return `${name} has been slain!`;
}

export function formatCombatEnded(): string {
  return "All enemies defeated!";
}

// ── N06: dialogue skill check feed line ───────────────────────────────────────

/**
 * Canonical one-line summary for a skill check rolled inside Ink dialogue.
 * Format: `"Mira: Persuasion check (DC 14) — 17. Success."`
 */
export function formatSkillCheck(
  speakerName: string,
  skill: string,
  dc: number,
  total: number,
  success: boolean,
): string {
  const outcome = success ? "Success" : "Failure";
  return `${speakerName}: ${skill} check (DC ${dc}) — ${total}. ${outcome}.`;
}

// ── E11: awareness feed formatters ────────────────────────────────────────────

/** Alert phase: enemy has lost its target and is scanning. */
export function formatEnemyAlerted(enemyName: string): string {
  return `${enemyName} looks around warily.`;
}

/** Alert phase end: the enemy gives up searching and returns to patrol. */
export function formatEnemyLostInterest(enemyName: string): string {
  return `${enemyName} loses interest.`;
}

/**
 * Neutral→hostile flip (the "drawing-weapons" beat — see E07). The `action`
 * argument lets NPCs flavour their escalation ("draws a cleaver", "raises
 * a staff"). Callers that don't care can pass the default phrase.
 */
export function formatFactionFlip(name: string, action = "draws a weapon"): string {
  return `${name} ${action}.`;
}

/**
 * Player "flee" — fired when the player steps out of an engaged enemy's
 * leash and combat disengages for that entity.
 */
export function formatPlayerDisengaged(): string {
  return "You disengage.";
}

/**
 * A spawner fired (monster-closet, Ink external, etc). `source` is the
 * in-world feature (`"the wall"`, `"the shadows"`, `"a rift"`, ...).
 */
export function formatMonstersSpawned(enemyName: string, source: string): string {
  return `${enemyName} emerge${enemyName.endsWith("s") ? "" : "s"} from ${source}.`;
}

/**
 * Pack aggro — one detected enemy roused `count` nearby allies. Rendered
 * as a single alarm line instead of per-ally "spotted you" spam.
 */
export function formatPackAlerted(enemyName: string, count: number): string {
  return `${enemyName} raises the alarm — ${count} more join the hunt!`;
}

/** W09 — a party member crossed an XP threshold. */
export function formatLevelUp(name: string, newLevel: number): string {
  return `${name} reaches level ${newLevel}!`;
}

// ── E11: auto-wire typed events to the feed ──────────────────────────────────

/**
 * Subscribe the feed to the awareness-event channel. Call once at boot.
 * Returns an unsubscribe handle so tests / hot-reload can tear down.
 *
 * The typed events (enemy:detected, enemy:alerted, enemy:faction_flipped,
 * enemy:died, enemy:spawned, perception:player_disengaged) are the
 * canonical emission surface for awareness UI. Any engine system that
 * wants a feed line for one of these categories should **emit the typed
 * event**, not call `pushFeed` directly — that way other subscribers
 * (achievements, LLM context, save-file replay) stay consistent.
 */
export function initFeedBus(): () => void {
  const unsub: Array<() => void> = [];

  unsub.push(
    EventBus.on("enemy:detected", ({ name }) => {
      pushFeed(formatEnemySpotted(name));
    }),
  );

  unsub.push(
    EventBus.on("enemy:alerted", ({ name, phase }) => {
      pushFeed(phase === "alerted" ? formatEnemyAlerted(name) : formatEnemyLostInterest(name));
    }),
  );

  unsub.push(
    EventBus.on("enemy:faction_flipped", ({ name, action }) => {
      pushFeed(formatFactionFlip(name, action));
    }),
  );

  unsub.push(
    EventBus.on("enemy:died", ({ name }) => {
      pushFeed(formatEntitySlain(name));
    }),
  );

  unsub.push(
    EventBus.on("enemy:spawned", ({ name, source }) => {
      pushFeed(formatMonstersSpawned(name, source));
    }),
  );

  unsub.push(
    EventBus.on("perception:player_disengaged", () => {
      pushFeed(formatPlayerDisengaged());
    }),
  );

  unsub.push(
    EventBus.on("enemy:pack_alerted", ({ byName, instanceIds }) => {
      pushFeed(formatPackAlerted(byName, instanceIds.length));
    }),
  );

  unsub.push(
    EventBus.on("world:loot_dropped", ({ source }) => {
      pushFeed(`${source} drops something.`);
    }),
  );

  unsub.push(
    EventBus.on("world:level_up", ({ name, newLevel }) => {
      pushFeed(formatLevelUp(name, newLevel));
    }),
  );

  return () => {
    for (const fn of unsub) fn();
  };
}
